import { AsyncAutocompleteSource } from '@components/v2/form/AsyncAutocomplete/asyncAutocomplete.types';
import { useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCustomerUnauthorizedMessages } from '../../../../hooks/useCustomerUnauthorizedMessages';
import useHandleApiError from '../../../../hooks/useHandleApiError';
import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { WithId } from '../../../../types/Base.types';
import { retrieveOrderProductFromUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { useTranslation } from '../../../translation/TranslationContext';
import { useOrderProductsContext } from '../../contexts/OrderProducts.context';
import { useBlockOrderWithoutCustomer } from '../../hooks/useBlockOrderWithoutCustomer';
import { CopyToNewOrderDTO } from '../../products/dtos/copy-to-new-order.dto';
import { BatchOrderProduct } from '../../products/interfaces/order-products-repository.types';
import { orderProductsRepository } from '../../products/orderProducts.repository';
import CopyOrderProductsBaseModal from '../components/CopyOrderProductsBaseModal/CopyOrderProductsBaseModal';
import {
  CopyOrderProductsToNewOrderFormFields,
  CopyOrderProductsToNewOrderModalProps,
} from './CopyOrderProductsToNewOrderModal.interfaces';
import CopyOrderProductsToNewOrderForm from './components/CopyOrderProductsToNewOrderForm/CopyOrderProductsToNewOrderForm';

const CUSTOMERS_TO_COPY_SOURCE: AsyncAutocompleteSource = {
  url: '/orders/info/customers-to-copy',
  limit: 20,
};

const CopyOrderProductsToNewOrderModal = ({
  isOpen,
  selectedItems,
  isAllItemsSelected,
  onClose,
}: CopyOrderProductsToNewOrderModalProps) => {
  const { id: orderId } = useParams<WithId>();
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const { getApiErrorMessage } = useHandleApiError();
  const { showUnauthorizedOrderProducts } = useCustomerUnauthorizedMessages();
  const { shouldBlockWithoutCustomer } = useBlockOrderWithoutCustomer();

  const { onRemoveProducts } = useOrderProductsContext();
  const repository = orderProductsRepository(orderId);

  const defaultValues: CopyOrderProductsToNewOrderFormFields = {
    customers: [],
    includeCurrentStore: false,
    removeFromOrigin: true,
  };

  const copyOrderProductsForm = useForm<CopyOrderProductsToNewOrderFormFields>({
    defaultValues,
  });

  const customers = useWatch({
    control: copyOrderProductsForm.control,
    name: 'customers',
  });

  const handleSubmit = async (values: CopyOrderProductsToNewOrderFormFields) => {
    const orderProducts: BatchOrderProduct[] = Array.from(selectedItems)
      .map((item) => retrieveOrderProductFromUniqueIdentifier(item))
      .filter((item) => item !== null);
    const data: CopyToNewOrderDTO = {
      filter: {
        orderProducts: isAllItemsSelected ? undefined : orderProducts,
        allOrderProducts: isAllItemsSelected,
      },
      options: { removeFromOrigin: values.removeFromOrigin },
      to: {
        customerIds: values.customers.map((customer) => customer.id),
      },
    };
    const response = await repository.copyToNewOrder(data);
    if (!response.ok) {
      const { code } = response.data || {};

      if (code === LangEnum.API_EXCEPTION_CUSTOMER_UNAUTHORIZED_TO_COPY_ORDER_PRODUCTS) {
        showUnauthorizedOrderProducts();
        return;
      }

      handleMessage({
        type: 'error',
        message: getApiErrorMessage(response, translate(LangEnum.COPY_TO_NEW_ORDER_ERROR_MESSAGE)),
      });
      copyOrderProductsForm.reset(values);
    } else {
      handleMessage({
        type: 'success',
        message: translate(LangEnum.COPY_TO_NEW_ORDER_SUCCESS_MESSAGE),
      });

      if (values.removeFromOrigin) {
        await onRemoveProducts(orderProducts);
      }
      onClose(values.removeFromOrigin);
    }
  };

  const isBlockedWithoutCustomer = shouldBlockWithoutCustomer && customers.length === 0;

  return (
    <CopyOrderProductsBaseModal<CopyOrderProductsToNewOrderFormFields>
      isOpen={isOpen}
      form={copyOrderProductsForm}
      title={LangEnum.COPY_ORDER_PRODUCTS_TO_NEW_ORDER}
      question={LangEnum.COPY_ORDER_PRODUCTS_TO_NEW_ORDER_QUESTION}
      description={LangEnum.COPY_ORDER_PRODUCTS_TO_NEW_ORDER_DESCRIPTION}
      hint={LangEnum.COPY_ORDER_PRODUCTS_TO_NEW_ORDER_HINT}
      isSubmitDisabled={isBlockedWithoutCustomer}
      handleSubmit={handleSubmit}
      onClose={onClose}
      copyButtonLabel={
        customers.length ? LangEnum.GLOBAL_COPY : LangEnum.GLOBAL_COPY_WITHOUT_CUSTOMER
      }
      submitButtonTooltipMessage={
        isBlockedWithoutCustomer ? LangEnum.BLOCK_COPY_ORDER_WITHOUT_CUSTOMER_ERROR : undefined
      }
    >
      <CopyOrderProductsToNewOrderForm
        source={CUSTOMERS_TO_COPY_SOURCE}
        form={copyOrderProductsForm}
        handleSubmit={handleSubmit}
      />
    </CopyOrderProductsBaseModal>
  );
};

export default CopyOrderProductsToNewOrderModal;
